// ./src/contexts/VehicleFinancingContext.tsx
//
// Context wrapper around the useVehicleFinancing hook.
//
// Purpose:
// - Allows sibling components (list, edit drawer, toolbar)
//   to share the same vehicle financing state without prop-drilling.
// - Each page creates its own <VehicleFinancingProvider> with its own config,
//   so VehicleFinancingPage filters don't affect DashboardPage, etc.
// - The underlying useVehicleFinancing hook remains the data engine; this context
//   simply makes its return value available to the component tree.
//
// Usage:
//
//   // In a page component:
//   <VehicleFinancingProvider autoLoad={false}>
//     <VehicleFinancingPageContent />
//     <VehicleFinancingEditDrawer />
//   </VehicleFinancingProvider>
//
//   // In any child component:
//   const { financingRecords, isLoading, create, ... } = useVehicleFinancingContext();
//
//   // In a component that may or may not be inside a provider (e.g. drawer):
//   const context = useOptionalVehicleFinancingContext();
//   const createFn = context?.create ?? props.onCreate;

import React, { createContext, useContext } from "react";
import {
  useVehicleFinancing,
  UseVehicleFinancingOptions,
  UseVehicleFinancingReturn,
} from "@/hooks/useVehicleFinancing";

// ============================================
// Context
// ============================================

const VehicleFinancingContext =
  createContext<UseVehicleFinancingReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface VehicleFinancingProviderProps
  extends UseVehicleFinancingOptions {
  children: React.ReactNode;
}

/**
 * Wraps useVehicleFinancing into a context provider.
 *
 * Each instance creates its own independent useVehicleFinancing store
 * with its own filters, pagination, loading state, and error state.
 */
export const VehicleFinancingProvider: React.FC<
  VehicleFinancingProviderProps
> = ({ children, ...options }) => {
  const vehicleFinancing = useVehicleFinancing(options);

  return (
    <VehicleFinancingContext.Provider value={vehicleFinancing}>
      {children}
    </VehicleFinancingContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

/**
 * Access the VehicleFinancingContext. Throws if used outside a VehicleFinancingProvider.
 */
export const useVehicleFinancingContext = (): UseVehicleFinancingReturn => {
  const context = useContext(VehicleFinancingContext);

  if (!context) {
    throw new Error(
      "useVehicleFinancingContext must be used within a <VehicleFinancingProvider>. " +
      "Wrap your component tree with <VehicleFinancingProvider> or use useOptionalVehicleFinancingContext() instead."
    );
  }

  return context;
};

/**
 * Access the VehicleFinancingContext if available, or return null.
 *
 * Use this in components that may or may not be inside a provider
 * (e.g. VehicleFinancingEditDrawer which works both inside the page's provider
 * and standalone on DashboardPage).
 */
export const useOptionalVehicleFinancingContext =
  (): UseVehicleFinancingReturn | null => {
    return useContext(VehicleFinancingContext);
  };